/**
 * BEEZFY PRODUCT CARD GALLERY
 * Enables swipe/click navigation through product images on cards
 */

// Guard: lo script può essere incluso più volte sulla stessa pagina (es. 2 sezioni
// related-products in PDP). Senza guard la seconda esecuzione ridichiara la classe
// → "Identifier 'BeeZFyCardGallery' has already been declared". Col guard il 2° load è no-op.
if (!window.BeeZFyCardGallery) {

class BeeZFyCardGallery {
  constructor(container) {
    this.container = container;
    this.gallery = container.querySelector('[data-card-gallery]');
    this.track = container.querySelector('[data-gallery-track]');
    this.slides = Array.from(container.querySelectorAll('[data-gallery-slide]'));
    this.dots = Array.from(container.querySelectorAll('[data-gallery-dot]'));
    this.link = container.querySelector('.bz-card__media-link');

    this.currentIndex = 0;
    this.isDragging = false;
    this.startX = 0;
    this.currentX = 0;
    this.threshold = 50; // Minimum swipe distance

    if (!this.gallery || this.slides.length <= 1) return;

    // Store instance reference on container for external access (e.g., swatch integration)
    container._bzfyGallery = this;

    this.init();
  }

  init() {
    // Dot navigation
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.goToSlide(index);
      });
    });

    // Frecce laterali (desktop): prev/next
    this.container.querySelectorAll('[data-gallery-arrow]').forEach((arrow) => {
      arrow.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (arrow.getAttribute('data-gallery-arrow') === 'next') this.next();
        else this.prev();
      });
    });

    // Video indicator - navigate to video slide
    const videoIndicator = this.container.querySelector('[data-video-indicator]');
    if (videoIndicator) {
      videoIndicator.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const videoSlide = parseInt(videoIndicator.getAttribute('data-video-slide') || '1');
        this.goToSlide(videoSlide);
      });
    }

    // Video click handlers - prevent navigation and toggle play/pause
    const videos = this.container.querySelectorAll('video');
    videos.forEach(video => {
      video.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (video.paused) {
          video.play();
        } else {
          video.pause();
        }
      });
    });

    // Touch events
    this.gallery.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
    this.gallery.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
    this.gallery.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true });

    // Mouse events (desktop)
    this.gallery.addEventListener('mousedown', this.handleMouseDown.bind(this));
    this.gallery.addEventListener('mousemove', this.handleMouseMove.bind(this));
    this.gallery.addEventListener('mouseup', this.handleMouseEnd.bind(this));
    this.gallery.addEventListener('mouseleave', this.handleMouseEnd.bind(this));

    // Prevent accidental link clicks when swiping
    this.link?.addEventListener('click', this.handleLinkClick.bind(this));
  }

  handleTouchStart(e) {
    this.isDragging = true;
    this.startX = e.touches[0].clientX;
    this.currentX = this.startX;
    this.gallery.setAttribute('data-dragging', 'true');
  }

  handleTouchMove(e) {
    if (!this.isDragging) return;

    this.currentX = e.touches[0].clientX;
    const diff = this.currentX - this.startX;

    // Prevent page scroll when swiping horizontally
    if (Math.abs(diff) > 10) {
      e.preventDefault();
    }
  }

  handleTouchEnd(e) {
    if (!this.isDragging) return;

    const diff = this.currentX - this.startX;

    if (Math.abs(diff) > this.threshold) {
      if (diff > 0) {
        this.prev();
      } else {
        this.next();
      }
    }

    this.isDragging = false;
    this.gallery.removeAttribute('data-dragging');
  }

  handleMouseDown(e) {
    // Only left click
    if (e.button !== 0) return;

    this.isDragging = true;
    this.startX = e.clientX;
    this.currentX = this.startX;
    this.gallery.setAttribute('data-dragging', 'true');
    e.preventDefault();
  }

  handleMouseMove(e) {
    if (!this.isDragging) return;
    this.currentX = e.clientX;
  }

  handleMouseEnd(e) {
    if (!this.isDragging) return;

    const diff = this.currentX - this.startX;

    if (Math.abs(diff) > this.threshold) {
      if (diff > 0) {
        this.prev();
      } else {
        this.next();
      }
    }

    this.isDragging = false;
    this.gallery.removeAttribute('data-dragging');
  }

  handleLinkClick(e) {
    // Prevent link navigation if user just swiped
    const diff = Math.abs(this.currentX - this.startX);
    if (diff > 5) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  next() {
    const nextIndex = (this.currentIndex + 1) % this.slides.length;
    this.goToSlide(nextIndex);
  }

  prev() {
    const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.goToSlide(prevIndex);
  }

  // Carica on-demand l'immagine di una slid (data-src -> src). La slide 0 ha gia'
  // il src reale; le altre restano data-src finche' l'utente non le raggiunge, cosi'
  // il load-more su mobile non tiene in memoria N immagini full-res per card.
  loadSlideImage(index) {
    const slide = this.slides[index];
    if (!slide) return;
    slide.querySelectorAll('img[data-lazy-slide][data-src]').forEach((img) => {
      if (img.dataset.srcset) img.setAttribute('srcset', img.dataset.srcset);
      img.setAttribute('src', img.dataset.src);
      img.removeAttribute('data-src');
      img.removeAttribute('data-srcset');
      img.removeAttribute('data-lazy-slide');
    });
  }

  goToSlide(index) {
    if (index === this.currentIndex || index < 0 || index >= this.slides.length) return;

    // Carica la slide di destinazione e precarica la successiva per una transizione fluida.
    this.loadSlideImage(index);
    this.loadSlideImage(index + 1);

    // Pause current video if playing
    const currentSlide = this.slides[this.currentIndex];
    const currentVideo = currentSlide?.querySelector('video');
    if (currentVideo && !currentVideo.paused) {
      currentVideo.pause();
      currentVideo.currentTime = 0; // Reset to start
    }

    // Update slides
    this.slides[this.currentIndex]?.classList.remove('bz-card__gallery-slide--active');
    this.slides[index]?.classList.add('bz-card__gallery-slide--active');

    // Update dots
    this.dots[this.currentIndex]?.classList.remove('bz-card__gallery-dot--active');
    this.dots[index]?.classList.add('bz-card__gallery-dot--active');

    this.currentIndex = index;

    // Autoplay video in new slide if it exists
    const newSlide = this.slides[index];
    const newVideo = newSlide?.querySelector('video[data-video-gallery]');
    if (newVideo) {
      // Ensure video has required attributes for iOS autoplay
      newVideo.setAttribute('playsinline', '');
      newVideo.setAttribute('muted', '');
      newVideo.muted = true;
      newVideo.playsInline = true;

      // Retry play multiple times to ensure it works
      const attemptPlay = () => {
        newVideo.currentTime = 0;

        // Try playing with retries - more aggressive on mobile
        const tryPlay = (attempt = 0) => {
          if (attempt > 10) {
            // If still failing after 10 attempts, try one more time with user gesture simulation
            console.log('Video autoplay failed after 10 attempts');
            return;
          }

          const playPromise = newVideo.play();

          if (playPromise !== undefined) {
            playPromise.then(() => {
              // Success - video is playing
              console.log('Video playing successfully');
            }).catch((error) => {
              console.log(`Play attempt ${attempt + 1} failed:`, error.message);

              if (attempt >= 10) {
                // All retries failed - add manual play button overlay
                this.addVideoPlayButton(newVideo);
              } else {
                // Retry after a delay - shorter delays on mobile
                setTimeout(() => tryPlay(attempt + 1), 50 * (attempt + 1));
              }
            });
          }
        };

        tryPlay();
      };

      // Force load and prepare video
      if (newVideo.readyState === 0) {
        newVideo.load();
      }

      // Handle based on video state with immediate retry
      if (newVideo.readyState >= 3) {
        // Video is ready, play immediately
        attemptPlay();
      } else if (newVideo.readyState >= 1) {
        // Has metadata, try playing
        attemptPlay();
        // Also listen for canplay as backup
        newVideo.addEventListener('canplay', attemptPlay, { once: true });
      } else {
        // Not loaded yet, wait for metadata
        newVideo.addEventListener('loadedmetadata', attemptPlay, { once: true });
        newVideo.addEventListener('canplay', attemptPlay, { once: true });
      }
    }

    // Dispatch event for analytics
    this.container.dispatchEvent(new CustomEvent('gallery:change', {
      detail: { index, total: this.slides.length }
    }));
  }

  addVideoPlayButton(video) {
    // Check if button already exists
    if (video.parentElement.querySelector('.bz-video-play-btn')) return;

    // Create play button overlay
    const playBtn = document.createElement('button');
    playBtn.className = 'bz-video-play-btn';
    playBtn.innerHTML = '<svg width="60" height="60" viewBox="0 0 60 60"><circle cx="30" cy="30" r="28" fill="rgba(255,255,255,0.9)"/><path d="M24 18l18 12-18 12z" fill="#000"/></svg>';
    playBtn.setAttribute('aria-label', 'Play video');

    playBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      video.play().then(() => {
        playBtn.remove();
      }).catch(() => {
        console.log('Manual play also failed');
      });
    });

    video.parentElement.style.position = 'relative';
    video.parentElement.appendChild(playBtn);

    // Add CSS for play button
    if (!document.getElementById('bz-video-play-btn-style')) {
      const style = document.createElement('style');
      style.id = 'bz-video-play-btn-style';
      style.textContent = `
        .bz-video-play-btn {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          border: none;
          background: transparent;
          cursor: pointer;
          z-index: 10;
          padding: 0;
          transition: transform 0.2s ease;
        }
        .bz-video-play-btn:hover {
          transform: translate(-50%, -50%) scale(1.1);
        }
        .bz-video-play-btn svg {
          filter: drop-shadow(0 2px 8px rgba(0,0,0,0.2));
        }
      `;
      document.head.appendChild(style);
    }
  }
}

// Initialize all galleries
function initCardGalleries() {
  const galleries = document.querySelectorAll('[data-gallery-enabled]');

  galleries.forEach(container => {
    if (!container.hasAttribute('data-gallery-initialized')) {
      new BeeZFyCardGallery(container);
      container.setAttribute('data-gallery-initialized', 'true');
    }
  });
}

// Auto-init on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCardGalleries);
} else {
  initCardGalleries();
}

// Re-init on dynamic content load (AJAX, Shopify sections)
document.addEventListener('shopify:section:load', initCardGalleries);
document.addEventListener('shopify:section:reorder', initCardGalleries);

// Expose to window for manual initialization
window.BeeZFyCardGallery = BeeZFyCardGallery;

} // fine guard if (!window.BeeZFyCardGallery)
window.initCardGalleries = initCardGalleries;
